import { useEffect, useMemo, useRef, useState } from "react";
import { useAnmeldung } from "../zustand/Anmeldung";
import { useDemo } from "../zustand/DemoZustand";
import useDokumentSitzung from "../haken/useDokumentSitzung";
import Textwerk, { farbeFuer } from "../bausteine/Textwerk";
import Randnotizen from "../bausteine/Randnotizen";
import Notizfeld from "../bausteine/Notizfeld";
import { Haken, Textmarker, Warnung } from "../bausteine/Symbole";
import { loeseVerankerung, verankere } from "../hilfen/dokumentmodell";
import { kanalZuTop } from "../hilfen/auswertung";
import { benenneDokument, setzeMuster } from "../dienst/server";

/*
  Ein Textdokument, offen.

  Alles, was mit dem Netz zu tun hat — laden, senden, empfangen, wer ist
  da, verdichten —, steckt in `useDokumentSitzung`. Hier steht nur noch,
  was ein TEXTdokument ausmacht: der Editor, die Kommentare am Rand, der
  Titel und das Muster-Kennzeichen.

  Warum das Gespräch zu einem Dokument im Kanal des Gremiums läuft und
  nicht hier: siehe KOMMUNIKATION.md. Am Dokument wird notiert, nicht
  geantwortet.
*/

export default function Textdokument({ dokumentId, oeffne }) {
  const { fraktion, token } = useAnmeldung();
  const { stammdaten, zustand, ich, aktionen } = useDemo();

  const {
    doc,
    satz,
    setzeSatz,
    laedt,
    fehler,
    setzeFehler,
    bereit,
    andere,
    meldeMarke,
  } = useDokumentSitzung(dokumentId);

  const [titel, setzeTitel] = useState("");
  /* Die gerade ausgewählte Textstelle — daraus wird ein Kommentar. */
  const [auswahl, setzeAuswahl] = useState(null);
  const [notizEntwurf, setzeNotizEntwurf] = useState(null);
  const [notizBearbeiten, setzeNotizBearbeiten] = useState(null);
  /* Zwingt ein Neuzeichnen, wenn sich der Text verschiebt. */
  const [takt, setzeTakt] = useState(0);

  const quillRef = useRef(null);

  /* Der Titel kommt aus der Sitzung, sobald sie geladen hat. */
  useEffect(() => {
    if (satz) setzeTitel(satz.titel);
  }, [satz]);

  /* ---------- Notizen am Dokument ---------- */

  /*
    Verankerungen auflösen: aus der von Yjs gespeicherten Stelle werden die
    Zahlen, die der Spiegel zum Hervorheben braucht.

    Das muss nach JEDER Änderung neu geschehen — schreibt jemand oben einen
    Satz ein, wandert alles darunter. Genau deshalb steht in der Datenbank
    die Verankerung und nicht die Zahl.
  */
  useEffect(() => {
    if (!bereit) return undefined;
    const beiAenderung = () => setzeTakt((n) => n + 1);
    doc.on("update", beiAenderung);
    return () => doc.off("update", beiAenderung);
  }, [bereit, doc]);

  const markierungen = useMemo(() => {
    if (!bereit) return [];
    return (zustand.annotationen || [])
      .filter(
        (a) =>
          a.dateiId === dokumentId &&
          (a.sichtbarkeit === "fraktion" || a.verfasserId === ich.id) &&
          a.notiz &&
          a.notiz.trim()
      )
      .map((a) => {
        const stelle = loeseVerankerung(doc, a.verankerung);
        /*
          Nicht mehr auflösbar heißt: Die Stelle wurde gelöscht. Der
          Kommentar bleibt am Rand stehen, er zeigt nur nirgendwo mehr hin —
          das ist ehrlicher, als ihn an eine geratene Stelle zu setzen.
        */
        return { ...a, stelle };
      })
      .sort((x, y) => (x.stelle ? x.stelle.start : 1e9) - (y.stelle ? y.stelle.start : 1e9));
    /* `takt` zwingt das Neurechnen nach jeder Änderung am Text. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bereit, zustand.annotationen, dokumentId, ich.id, doc, takt]);

  /*
    Aus einer ausgewählten Stelle einen Kommentar machen.

    Verankert wird über den `Y.Text` des Dokuments — nicht über eine Zahl.
    Damit bleibt der Kommentar an seinem Wort, auch wenn jemand davor
    schreibt.
  */
  async function notizSichern(text) {
    const stelle = notizEntwurf;
    if (!stelle) return;
    try {
      await aktionen.fuegeAnnotationHinzu({
        dateiId: dokumentId,
        absatzId: "",
        start: stelle.von,
        ende: stelle.bis,
        zitat: stelle.zitat,
        notiz: text,
        sichtbarkeit: "fraktion",
        farbe: "gelb",
        verankerung: verankere(doc.getText("quill"), stelle.von, stelle.bis),
      });
      setzeNotizEntwurf(null);
      setzeAuswahl(null);
    } catch (f) {
      setzeFehler(f.message);
    }
  }

  /* Vom Kommentar am Rand zurück in den Text. */
  function springeZu(notiz) {
    const quill = quillRef.current;
    if (!quill || !notiz.stelle) return;
    quill.setSelection(notiz.stelle.start, notiz.stelle.ende - notiz.stelle.start, "user");
    quill.focus();
  }

  /* ---------- Muster ---------- */

  /*
    Ein Muster ist dasselbe Dokument, nur gekennzeichnet. Der Knopf ändert
    nichts am Text — er sagt nur: Aus diesem hier darf man neue anfangen.
  */
  async function musterUmschalten() {
    if (!satz) return;
    try {
      setzeSatz(await setzeMuster(token, dokumentId, !satz.muster));
    } catch (f) {
      setzeFehler(f.message);
    }
  }

  /* ---------- Titel ---------- */

  async function sichereTitel() {
    const neu = titel.trim();
    if (!neu || !satz || neu === satz.titel) {
      setzeTitel(satz ? satz.titel : "");
      return;
    }
    try {
      const geaendert = await benenneDokument(token, dokumentId, neu);
      setzeSatz(geaendert);
    } catch (f) {
      setzeFehler(f.message);
      setzeTitel(satz.titel);
    }
  }

  if (laedt) {
    return <p className="leise" style={{ marginTop: "var(--luft-5)" }}>Wird geöffnet …</p>;
  }

  if (fehler && !satz) {
    return (
      <div className="karte" style={{ marginTop: "var(--luft-5)" }}>
        <p className="tor__fehler">
          <Warnung groesse={18} />
          <span>{fehler}</span>
        </p>
        <button
          className="knopf knopf--zurueckhaltend"
          style={{ marginTop: "var(--luft-4)" }}
          onClick={() => oeffne("office", {})}
        >
          Zurück zu Office
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="detailkopf nicht-drucken">
        <p className="detailkopf__vorspann">
          {fraktion ? `${fraktion.name} · ${fraktion.kommune}` : "Fraktion"}
        </p>

        <div className="dokumentkopf">
          <input
            className="dokumenttitel"
            value={titel}
            onChange={(e) => setzeTitel(e.target.value)}
            onBlur={sichereTitel}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
            aria-label="Titel des Dokuments"
          />

          {/*
            Wer sonst noch da ist, steht oben rechts als Kreis mit Kürzel —
            in derselben Farbe wie seine Schreibmarke im Text. So erkennt man
            beides als dieselbe Person wieder.
          */}
          <div className="dokumentkopf__leute">
            {andere.map((p) => (
              <span
                key={p.kontoId}
                className="leutekreis"
                style={{ background: p.farbe || farbeFuer(p.kontoId) }}
                title={`${p.name} ist im Dokument`}
              >
                {kuerzel(p.name)}
              </span>
            ))}
            <button
              className={`knopf knopf--schlicht ${satz && satz.muster ? "knopf--an" : ""}`}
              onClick={musterUmschalten}
              title={
                satz && satz.muster
                  ? "Aus diesem Dokument lassen sich neue anlegen"
                  : "Sichert dieses Dokument als Muster — Briefkopf, Musterantrag"
              }
            >
              <Haken groesse={16} />
              {satz && satz.muster ? "Ist ein Muster" : "Als Muster sichern"}
            </button>

            <button className="knopf knopf--schlicht" onClick={() => window.print()}>
              Drucken
            </button>
          </div>
        </div>

        {fehler && (
          <p className="klein" style={{ color: "var(--ampel-nein)" }} role="alert">
            {fehler}
          </p>
        )}
      </div>

      {/*
        Blatt und Rand nebeneinander — wie bei Google. Auf schmalen Geräten
        rutschen die Notizen unter das Blatt, sonst bliebe für den Text
        nichts übrig.
      */}
      <Textwerk
        doc={doc}
        praesenz={andere}
        aufBereit={(quill) => {
          quillRef.current = quill;
        }}
        aufAuswahl={(a) => {
          setzeAuswahl(a);
          meldeMarke(a);
        }}
        rand={
          <Randnotizen
            markierungen={markierungen}
            aufSpringen={springeZu}
            aufBearbeiten={setzeNotizBearbeiten}
            aufInDenChat={(notiz) =>
              oeffne("kanal", { kanalId: kanalZuTop(stammdaten, null), zitat: notiz.id })
            }
          />
        }
      />

      {auswahl && auswahl.bis > auswahl.von && !notizEntwurf && (
        <button
          className="notizknopf nicht-drucken"
          style={{ left: auswahl.stelle.randX ?? auswahl.stelle.x, top: auswahl.stelle.y }}
          title="Kommentar zu dieser Stelle"
          aria-label="Kommentar zu dieser Stelle"
          onClick={() => setzeNotizEntwurf(auswahl)}
        >
          <Textmarker groesse={18} />
        </button>
      )}

      {notizEntwurf && (
        <Notizfeld
          zitat={notizEntwurf.zitat}
          stelle={notizEntwurf.stelle}
          aufSichern={notizSichern}
          aufAbbrechen={() => {
            setzeNotizEntwurf(null);
            setzeAuswahl(null);
          }}
        />
      )}

      {notizBearbeiten && (
        <Notizfeld
          zitat={notizBearbeiten.zitat}
          /*
            KEINE Stelle — und das ist Absicht.

            `platziere` in `Notizfeld.js` erwartet Bildschirmkoordinaten als
            `{ links, oben, unten }`. Hier stand vorher `{ x, y }`. Die
            Funktion fand ihre Felder nicht, rechnete mit `undefined` weiter
            und lieferte `left: "undefinedpx"` und `bottom: "NaNpx"` — beides
            wirft der Browser stillschweigend weg. Das Feld landete also
            nicht mittig, sondern gar nicht platziert, irgendwo.

            Ohne Angabe nimmt `platziere` ihren ausdrücklich vorgesehenen
            Rückfallwert und stellt das Feld mittig. Beim BEARBEITEN einer
            Notiz ist das auch richtig: Man kommt vom Rand, nicht von einer
            Textstelle — es gibt keinen Ort, an den es gehörte. Präsentation
            und Tabelle machen es genauso.
          */
          wert={notizBearbeiten.notiz}
          aufSichern={async (text) => {
            await aktionen.setzeAnnotationNotiz(notizBearbeiten.id, text);
            setzeNotizBearbeiten(null);
          }}
          aufEntfernen={async () => {
            await aktionen.entferneAnnotation(notizBearbeiten.id);
            setzeNotizBearbeiten(null);
          }}
          aufAbbrechen={() => setzeNotizBearbeiten(null)}
        />
      )}
    </>
  );
}

/* „Renate Buschmann“ → „RB“ */
function kuerzel(name) {
  return String(name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((teil) => teil.charAt(0).toUpperCase())
    .join("");
}
